/**
 * The rosters of docs/sample-drive.md as combatants: real base stats, level 50, IVs 31 (the sim's
 * stat formula), Play 1's slots. Shared by the S2 acceptance tests and the harness.
 */
import offenseJson from '@tdmons/data/plays/off-shotgun-trips-right-quick-slants.json';
import defenseJson from '@tdmons/data/plays/def-cover-2-zone.json';
import { parseDefensePlay, parseOffensePlay } from '../../src/playdef';
import { ppAfterDivisor } from '../../src/balance';
import { statsAtLevel } from '../../src/stats';
import type {
  Combatant,
  GameState,
  Move,
  MoveCategory,
  MoveEffect,
  PlayCall,
  Position,
  Stats,
  Team,
  TeamId,
  TypeName,
} from '../../src/types';

/** Found by searching mulberry32 seeds for no crits and the Play 1 story; see docs/sample-drive.md. */
export const PLAY1_SEED = 7;

function move(
  id: string,
  name: string,
  type: TypeName,
  category: MoveCategory,
  power: number,
  dexPP: number,
  effect: MoveEffect = { kind: 'none' },
  priority = 0,
): Move {
  return {
    id,
    name,
    type,
    category,
    power,
    accuracy: 1,
    pp: ppAfterDivisor(dexPP),
    priority,
    effect,
  };
}

const quickAttack = move(
  'quickattack',
  'Quick Attack',
  'Normal',
  'Physical',
  40,
  30,
  { kind: 'burst', speedStages: 1 },
  1,
);
const agility = move('agility', 'Agility', 'Psychic', 'Status', 0, 30, {
  kind: 'stage',
  stat: 'spe',
  stages: 2,
});

export function mon(
  name: string,
  types: [TypeName] | [TypeName, TypeName],
  [hp, atk, def, spa, spd, spe]: [number, number, number, number, number, number],
  position: Position,
  moves: Move[] = [],
): Combatant {
  const base: Stats = { hp, atk, def, spa, spd, spe };
  const stats = statsAtLevel(base, 50);
  return {
    creatureId: name.toLowerCase(),
    name,
    types,
    level: 50,
    stats,
    position,
    moves,
    ppLeft: Object.fromEntries(moves.map((m) => [m.id, m.pp])),
    hp: stats.hp,
    stages: {},
    enteredThisPlay: false,
  };
}

export function play1State(): GameState {
  return {
    offense: {
      lineup: {
        QB: mon('Alakazam', ['Psychic'], [55, 50, 45, 135, 95, 120], 'QB'),
        RB: mon('Arcanine', ['Fire'], [90, 110, 80, 100, 80, 95], 'RB'),
        WR1: mon('Pidgeot', ['Normal', 'Flying'], [83, 80, 75, 70, 70, 101], 'WR', [
          agility,
          quickAttack,
        ]),
        WR2: mon('Jolteon', ['Electric'], [65, 65, 60, 110, 95, 130], 'WR', [quickAttack, agility]),
        TE: mon('Machamp', ['Fighting'], [90, 130, 80, 65, 85, 55], 'TE'),
        OL1: mon('Steelix', ['Steel', 'Ground'], [75, 85, 200, 55, 65, 30], 'OL'),
        OL2: mon('Snorlax', ['Normal'], [160, 110, 65, 65, 110, 30], 'OL'),
      },
    },
    defense: {
      lineup: {
        DL1: mon('Rhydon', ['Ground', 'Rock'], [105, 130, 120, 45, 45, 40], 'DL'),
        DL2: mon('Golem', ['Rock', 'Ground'], [80, 120, 130, 55, 65, 45], 'DL'),
        LB1: mon('Primeape', ['Fighting'], [65, 105, 60, 60, 70, 95], 'LB'),
        LB2: mon('Tauros', ['Normal'], [75, 100, 95, 40, 70, 110], 'LB'),
        CB1: mon('Raichu', ['Electric'], [60, 90, 55, 90, 80, 110], 'CB'),
        CB2: mon('Starmie', ['Water', 'Psychic'], [60, 75, 85, 100, 85, 115], 'CB'),
        S: mon('Gengar', ['Ghost', 'Poison'], [60, 65, 60, 130, 75, 110], 'S'),
      },
    },
    ballOn: 25,
    down: 1,
    toGo: 10,
  };
}

export const quickSlants = parseOffensePlay(offenseJson);
export const cover2 = parseDefensePlay(defenseJson);

/** Play 1's decisions: quick slants, Jolteon spends Quick Attack (the play's default), normal tempo. */
export const play1Call: PlayCall = {
  offense: quickSlants,
  defense: cover2,
  offenseMoves: { WR2: 'quickattack' },
  tempo: 'normal',
};

/**
 * The two Play 1 rosters as whole teams for the game loop (slice 6). Each plays both ways from one
 * roster: Team Alakazam is Play 1's offense seven, Team Gengar its defense seven, and each gets a
 * hand-picked chart for the other side of the ball. Roster index = Play 1 slot order.
 */
export function play1Teams(): Record<TeamId, Team> {
  const s = play1State();
  const o = s.offense.lineup;
  const d = s.defense.lineup;
  return {
    A: {
      id: 'A',
      name: 'Team Alakazam',
      // QB Alakazam, RB Arcanine, WR Pidgeot, WR Jolteon, TE Machamp, OL Steelix, OL Snorlax
      roster: [o.QB, o.RB, o.WR1, o.WR2, o.TE, o.OL1, o.OL2],
      offense: { QB: 0, RB: 1, WR1: 2, WR2: 3, TE: 4, OL1: 5, OL2: 6 },
      defense: { DL1: 5, DL2: 6, LB1: 4, LB2: 1, CB1: 3, CB2: 2, S: 0 },
    },
    B: {
      id: 'B',
      name: 'Team Gengar',
      // DL Rhydon, DL Golem, LB Primeape, LB Tauros, CB Raichu, CB Starmie, S Gengar
      roster: [d.DL1, d.DL2, d.LB1, d.LB2, d.CB1, d.CB2, d.S],
      offense: { QB: 6, RB: 3, WR1: 4, WR2: 5, TE: 2, OL1: 0, OL2: 1 },
      defense: { DL1: 0, DL2: 1, LB1: 2, LB2: 3, CB1: 4, CB2: 5, S: 6 },
    },
  };
}
